import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Gamepad, Sparkles, Mail, Phone, MessageSquare, Code, Zap, Users, Target, Shield, Lightbulb, Rocket, ArrowRight, CheckCircle2, Layers, Bell, ShieldCheck, TrendingUp, CreditCard } from 'lucide-react';
import SEO from '../components/SEO';
import { ROUTES } from '../components/Navigation/routes';

// mediaSrc may be a bare filename (served from /images) or a full URL (e.g. external asset storage).
export const resolveMediaSrc = (mediaSrc) => (/^https?:\/\//.test(mediaSrc) ? mediaSrc : `/images/${mediaSrc}`);

export const CardContent = ({ title, subtitle, description, bullets, outcome, mediaSrc, mediaType, solutionPartner, cta }) => {
  return (
    <div className="bg-white border-2 border-purple-200 rounded-2xl overflow-hidden hover:border-purple-400 transition-all duration-300 shadow-lg hover:shadow-xl min-w-0">
      <div className="p-6 sm:p-8 space-y-6">
        <div>
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-800">{title}</h3>
          <p className="text-purple-500 mt-1 text-base font-medium">{subtitle}</p>
          <p className="text-gray-600 mt-3 text-lg leading-relaxed">{description}</p>
        </div>

        {bullets && bullets.length > 0 && (
          <ul className="space-y-3">
            {bullets.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-700">
                <ArrowRight className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        )}

        {solutionPartner && (
          <p className="text-sm text-gray-500">
            Solution partner: <span className="font-semibold text-gray-700">{solutionPartner}</span>
          </p>
        )}

        {cta && (
          <a
            href={cta.href}
            className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors no-underline w-fit"
          >
            {cta.label} <ArrowRight className="w-3.5 h-3.5" />
          </a>
        )}

        <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-6">
          <h4 className="text-lg font-semibold text-purple-600 mb-2">Outcome</h4>
          <p className="text-gray-700 leading-relaxed">{outcome}</p>
        </div>

        {mediaSrc && (
          <div className="rounded-xl overflow-hidden border-2 border-purple-200 bg-gray-50">
            {mediaType === 'video' ? (
              <video src={resolveMediaSrc(mediaSrc)} controls className="w-full">
                Your browser does not support the video tag.
              </video>
            ) : (
              <img src={resolveMediaSrc(mediaSrc)} alt={title} className="w-full object-cover" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// The 3 core service pillars — reused on the homepage and as section anchors here.
export const services = [
  {
    id: 'custom-development',
    icon: <Code className="w-8 h-8" />,
    title: "Custom Development",
    description: "Bespoke app development, AI/ML engineering, and system architecture designed for your unique needs."
  },
  {
    id: 'workflow-automation',
    icon: <Zap className="w-8 h-8" />,
    title: "Workflow Automation",
    description: "Next-generation ERP and intelligent automation solutions that scale with your business."
  },
  {
    id: 'managed-consulting',
    icon: <Users className="w-8 h-8" />,
    title: "Managed Consulting",
    description: "AI/Product development, system architecture, and fully maintained, results-driven solutions."
  }
];

export const valueProps = [
  {
    icon: <Target className="w-6 h-6" />,
    title: "No Vapourware",
    description: "Real, functional solutions that deliver tangible results"
  },
  {
    icon: <Shield className="w-6 h-6" />,
    title: "No Lock-in",
    description: "Freedom and flexibility in every partnership"
  },
  {
    icon: <Lightbulb className="w-6 h-6" />,
    title: "Enablement-First",
    description: "Empowering your team for long-term success"
  }
];

// Concrete solution examples, grouped under their matching service pillar (see `pillar`).
export const whatWeDo = [
  {
    pillar: 'custom-development',
    title: 'Digital Twin',
    subtitle: 'Datacenter',
    description: 'We build spatial digital twins for data centers, capturing the facility as-built and keeping it synced with design intent throughout construction and operations.',
    bullets: [
      '360° Data Center Spatial twin',
      'Chronological System records',
      'Validate and compare against BIM',
      'Progress and quality records',
      'Handover as-built',
      'Operations & FM Reference'
    ],
    outcome: 'A continuously updated spatial record of the data center that bridges design, construction, and operations.',
    mediaSrc: 'https://qrlxishrjxuufbjrtokh.supabase.co/storage/v1/object/public/tool-assets/digital-twin.webp',
    mediaType: 'image',
    solutionPartner: 'Cupix',
    cta: { label: 'Get Quote', href: 'mailto:contact@stray-apps.com' }
  },
  {
    pillar: 'custom-development',
    title: 'AI-Assisted Generative Design',
    subtitle: 'Rapid Concept Development within Regulatory Constraints',
    description: 'We build intelligent design systems that generate layout options compliant by design, supporting residential, commercial, industrial, and mixed-use projects.',
    bullets: [
      'Site boundary conditions',
      'Development control parameters',
      'Building typology requirements',
      'Unit mix or operational constraints'
    ],
    outcome: 'Concept design reduced from hours or days to minutes — with options that are compliant by design.',
    mediaSrc: 'constech2.png',
    mediaType: 'image'
  },
  {
    pillar: 'custom-development',
    title: 'BIM Automation & Digital Delivery',
    subtitle: 'From Parametric Inputs to Coordinated BIM Models',
    description: 'We develop automation pipelines connecting frontend planning tools with downstream BIM, procurement, and fabrication environments.',
    bullets: [
      'Convert structured design data into BIM models',
      'Generate coordinated drawings and schedules',
      'Support IDD workflows and digital submissions',
      'Integrate with fabrication and DfMA processes'
    ],
    outcome: 'Improved model accuracy, reduced manual drafting effort, and seamless transition from concept to construction documentation.',
    mediaSrc: 'constech3.MP4',
    mediaType: 'video'
  },

  {
    pillar: 'workflow-automation',
    title: 'Intelligent Project & ERP Automation',
    subtitle: 'Workflow Orchestration for Construction Enterprises',
    description: 'Construction and real estate organisations operate across procurement, contracts, finance, HR, and site operations. Traditional ERP systems are rigid and costly to modify.',
    bullets: [
      'Digitise approval processes',
      'Automate procurement and contract routing',
      'Monitor SLA and compliance thresholds',
      'Integrate with existing ERP and accounting systems'
    ],
    outcome: 'Reduced process turnaround time, improved governance, and greater operational transparency.',
    mediaSrc: 'constech4.png',
    mediaType: 'image'
  },
  {
    pillar: 'managed-consulting',
    title: 'Planning & Regulatory Intelligence',
    subtitle: 'Automating Development Control & Site Analysis',
    description: 'We develop platforms that integrate with national mapping systems (e.g., OneMap) and planning frameworks to automatically retrieve and interpret site-specific regulatory constraints.',
    bullets: [
      'Zoning and land use controls',
      'Plot ratio and GFA constraints',
      'Building height and setback requirements',
      'Envelope controls and special planning areas',
      'Heritage and conservation overlays'
    ],
    outcome: 'Faster feasibility studies, reduced compliance risk, and earlier validation against URA and local authority guidelines.',
    mediaSrc: 'constech1.png',
    mediaType: 'image'
  },
  {
    pillar: 'workflow-automation',
    title: 'AI-Powered Decision Support',
    subtitle: 'Smart Automation Across the Building Lifecycle',
    description: 'We embed AI into operational workflows to support decision-making. Every decision is traceable and auditable — supporting governance requirements across regulated environments.',
    bullets: [
      'Procurement approvals and anomaly detection',
      'Cost forecasting and budget tracking',
      'Document classification and contract review',
      'Exception handling and case-based retrieval',
      'Asset performance analytics'
    ],
    outcome: 'Higher straight-through processing, faster approvals, and reduced manual workload.',
    mediaSrc: 'constech5.png',
    mediaType: 'image'
  },
  {
    pillar: 'workflow-automation',
    title: 'Forecast Engine',
    subtitle: 'ML-Powered Demand Forecasting for Inventory Management',
    description: 'Uses Facebook Prophet for time-series prediction with seasonality detection and promotional impact analysis.',
    bullets: [
      'Demand prediction with confidence intervals',
      'Promotional impact detection',
      'Automated reorder recommendations',
      'Forecast quality metrics (MAPE, MAE, RMSE)'
    ],
    outcome: 'Accurate demand forecasting that reduces overstock and stockouts, enabling smarter inventory decisions.',
    mediaSrc: 'forecastapp.png',
    mediaType: 'image'
  },
  {
    pillar: 'custom-development',
    title: 'AI Gift Commerce Platform',
    subtitle: 'Full-Stack AI-Powered Recommendation & E-Commerce',
    description: 'Users create sessions, add recipients with preferences, and receive personalised product suggestions generated via LLM — then checkout seamlessly with integrated payments.',
    bullets: [
      'AI-generated personalised recommendations per recipient',
      'Multi-recipient session management with stage tracking',
      'Stripe payment processing with saved cards',
      'Bulk recipient upload via spreadsheet',
      'Full order history and activity audit logging'
    ],
    outcome: 'A seamless gifting experience powered by AI that personalises every recommendation and streamlines the entire purchase flow.',
    mediaSrc: 'greet.png',
    mediaType: 'image'
  }
];

// Industry solution pages linked from this page — content mirrors each page's own hero copy.
const industrySolutions = [
  {
    id: 'construction-tech',
    route: ROUTES.CONSTRUCTION_TECH,
    title: 'Built Environment Technology Solutions',
    subtitle: 'Digital Solutions for Construction & Real Estate Sector',
    description: "We design and build digital platforms for the built environment ecosystem — supporting developers, architects, engineers, main contractors, and asset owners. We help organisations streamline compliance, accelerate design workflows, automate operations, and improve decision-making across the entire building lifecycle.",
    accent: 'from-blue-400 to-purple-500',
    highlights: [
      { label: 'Zoning check passed in seconds', icon: CheckCircle2 },
      { label: 'BIM model synced automatically', icon: Layers },
      { label: 'Approval routed to site manager', icon: Bell }
    ],
    toolStack: [
      { name: 'Autodesk Revit', logo: 'autodeskrevit.svg' },
      { name: 'IFC', logo: 'ifc.svg' },
      { name: 'Blender', logo: 'blender.svg' },
      { name: 'That Open', logo: 'thatopen.svg' },
      { name: 'Google Cloud', logo: 'googlecloud.svg' },
      { name: 'PostgreSQL', logo: 'postgresql.svg' },
      { name: 'Python', logo: 'python.svg' },
      { name: 'Node.js', logo: 'nodedotjs.svg' },
      { name: 'React', logo: 'react.svg' },
      { name: 'Claude Code', logo: 'claudecode.svg' }
    ]
  },
  {
    id: 'fintech',
    route: ROUTES.FINTECH,
    title: 'Ecommerce Solutions',
    subtitle: null,
    description: 'End-to-end digital commerce platforms with intelligent analytics, payment automation, and ML-powered demand forecasting.',
    accent: 'from-emerald-400 to-teal-500',
    highlights: [
      { label: 'Fraud pattern flagged instantly', icon: ShieldCheck },
      { label: 'Demand forecast accuracy +32%', icon: TrendingUp },
      { label: 'One-tap checkout completed', icon: CreditCard }
    ],
    toolStack: [
      { name: 'Stripe', logo: 'stripe.svg' },
      { name: 'Meta', logo: 'meta.svg' },
      { name: 'TensorFlow', logo: 'tensorflow.svg' },
      { name: 'Python', logo: 'python.svg' },
      { name: 'Node.js', logo: 'nodedotjs.svg' },
      { name: 'React', logo: 'react.svg' },
      { name: 'iOS', logo: 'ios.svg' },
      { name: 'Android', logo: 'android.svg' },
      { name: 'Claude Code', logo: 'claudecode.svg' }
    ]
  },
  {
    id: 'creative-industries',
    route: ROUTES.CREATIVE_INDUSTRIES,
    title: 'Creative Industries Solutions',
    subtitle: null,
    description: 'Purpose-built digital products for creators, media teams, and entertainment platforms.',
    accent: 'from-pink-400 to-orange-400',
    highlights: [
      { label: 'Assets auto-tagged by AI', icon: Sparkles },
      { label: 'Render queue optimized', icon: Layers },
      { label: 'Audience engagement +18%', icon: TrendingUp }
    ],
    toolStack: [
      { name: 'Figma', logo: 'figma.svg' },
      { name: 'Unity', logo: 'unity.svg' },
      { name: 'Blender', logo: 'blender.svg' },
      { name: 'Hugging Face', logo: 'huggingface.svg' },
      { name: 'Node.js', logo: 'nodedotjs.svg' },
      { name: 'React', logo: 'react.svg' },
      { name: 'iOS', logo: 'ios.svg' },
      { name: 'Android', logo: 'android.svg' },
      { name: 'Claude Code', logo: 'claudecode.svg' }
    ]
  }
];

// Horizontal logo ribbon of the tool stack behind a solution.
const ToolStackRibbon = ({ tools }) => (
  <div className="mt-6 pt-5 border-t border-purple-100 max-w-full">
    <p className="text-xs font-semibold tracking-wide text-gray-400 uppercase mb-3">Tool stack</p>
    <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
      {tools.map((tool) => (
        <div
          key={tool.name}
          className="flex items-center gap-1.5 grayscale opacity-60 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
          title={tool.name}
        >
          <img src={`/images/logos/${tool.logo}`} alt={tool.name} className="h-5 w-5 object-contain shrink-0" />
          <span className="text-sm font-medium text-gray-600 whitespace-nowrap">{tool.name}</span>
        </div>
      ))}
    </div>
  </div>
);

// Web + mobile app frame mockup with floating tooltips that cycle through industry highlights.
const IndustryPreview = ({ highlights, accent, flip }) => {
  const spots = ['top-0 -left-6 sm:-left-10', 'top-1/3 -right-6 sm:-right-10', 'bottom-24 -left-4 sm:-left-8'];
  const [tooltipIndex, setTooltipIndex] = useState(0);
  const [spotIndex, setSpotIndex] = useState(0);

  useEffect(() => {
    const pickDifferent = (max, current) => {
      if (max <= 1) return 0;
      let next = Math.floor(Math.random() * max);
      while (next === current) next = Math.floor(Math.random() * max);
      return next;
    };

    const interval = setInterval(() => {
      setTooltipIndex((prev) => pickDifferent(highlights.length, prev));
      setSpotIndex((prev) => pickDifferent(spots.length, prev));
    }, 2600);

    return () => clearInterval(interval);
  }, [highlights.length, spots.length]);

  const current = highlights[tooltipIndex];
  const CurrentIcon = current.icon;

  return (
    <div className="relative w-full max-w-[440px] mx-auto py-10">
      {/* Web app frame */}
      <div className={`relative rounded-2xl border-2 border-purple-200 bg-white shadow-xl overflow-hidden ${flip ? '-rotate-2' : 'rotate-2'}`}>
        <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-purple-100">
          <span className="w-3 h-3 rounded-full bg-red-300" />
          <span className="w-3 h-3 rounded-full bg-yellow-300" />
          <span className="w-3 h-3 rounded-full bg-green-300" />
          <span className="ml-2 h-4 flex-1 rounded-full bg-white border border-gray-200" />
        </div>
        <div className={`p-6 space-y-4 bg-gradient-to-br ${accent}`}>
          <div className="h-4 w-2/3 rounded-full bg-white/70" />
          <div className="h-4 w-1/2 rounded-full bg-white/50" />
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="h-20 rounded-lg bg-white/60" />
            <div className="h-20 rounded-lg bg-white/40" />
            <div className="h-20 rounded-lg bg-white/60" />
          </div>
          <div className="h-2.5 w-full rounded-full bg-white/40" />
          <div className="h-2.5 w-4/5 rounded-full bg-white/30" />
        </div>
      </div>

      {/* Mobile app frame — fixed 2:5 aspect ratio */}
      <div
        className={`absolute -bottom-10 ${flip ? '-left-6 sm:-left-10' : '-right-6 sm:-right-10'} w-32 sm:w-40 aspect-[3/5] rounded-[1.8rem] border-4 border-gray-800 bg-white shadow-xl overflow-hidden flex flex-col ${flip ? 'rotate-3' : '-rotate-3'}`}
      >
        <div className="h-4 shrink-0 bg-gray-800 flex items-center justify-center">
          <div className="w-10 h-1 rounded-full bg-gray-600" />
        </div>
        <div className={`flex-1 p-3 space-y-2 bg-gradient-to-b ${accent}`}>
          <div className="h-2.5 w-2/3 rounded-full bg-white/70" />
          <div className="h-16 rounded-lg bg-white/50" />
          <div className="h-8 rounded-lg bg-white/40" />
          <div className="h-8 rounded-lg bg-white/60" />
          <div className="h-8 rounded-lg bg-white/40" />
        </div>
      </div>

      {/* Floating annotation tooltip — flips between highlights and positions */}
      <div className={`absolute ${spots[spotIndex]} z-10 pointer-events-none`}>
        <AnimatePresence mode="wait">
          <motion.div
            key={`${tooltipIndex}-${spotIndex}`}
            initial={{ opacity: 0, y: 8, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.9 }}
            transition={{ duration: 0.4 }}
            className="flex items-center gap-2 bg-white border-2 border-purple-200 rounded-full shadow-lg pl-2.5 pr-4 py-2 max-w-[220px]"
          >
            <span className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white flex items-center justify-center shrink-0">
              <CurrentIcon className="w-4 h-4" />
            </span>
            <span className="text-sm font-semibold text-gray-700 leading-tight">{current.label}</span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// One industry solution's summary card, its app preview, and inline request/explore actions.
const IndustrySolutionSection = ({ solution, index }) => {
  const flip = index % 2 === 1;

  return (
    <div id={solution.id} className="scroll-mt-28 mb-24">
      <div className={`flex flex-col ${flip ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-6 md:gap-14`}>
        <div className="flex-1 min-w-0">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-800">{solution.title}</h3>
          {solution.subtitle && (
            <p className="text-purple-500 mt-1 text-base font-medium">{solution.subtitle}</p>
          )}
          <p className="text-gray-600 mt-3 text-lg leading-relaxed">{solution.description}</p>

          <ToolStackRibbon tools={solution.toolStack} />

          <div className="flex flex-wrap items-center gap-4 mt-6">
            <Link
              to={ROUTES.CONTACT}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors whitespace-nowrap no-underline"
            >
              <MessageSquare className="w-4 h-4" /> Send Request
            </Link>
            <Link
              to={solution.route}
              className="inline-flex items-center gap-2 text-purple-600 font-semibold hover:text-purple-700 transition-colors no-underline"
            >
              More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="flex-1 min-w-0 w-full">
          <IndustryPreview highlights={solution.highlights} accent={solution.accent} flip={flip} />
        </div>
      </div>
    </div>
  );
};

const Services = () => {
  const navigate = useNavigate();

  const ventureSteps = [
    { number: "01", title: "Co-Found", description: "Collaborate on groundbreaking ideas" },
    { number: "02", title: "Build", description: "Develop with top-tier talent" },
    { number: "03", title: "Operate", description: "Scale with proven strategies" },
    { number: "04", title: "Exit", description: "Achieve successful outcomes" }
  ];

  const scrollToPillar = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <SEO
        title="Services — Custom Development, Workflow Automation & Managed Consulting | STRAY Apps"
        description="From custom development to managed consulting, STRAY Apps delivers transparent, functional software and scalable business solutions — including AEC planning intelligence, generative design, BIM automation, and ERP workflow automation."
        path="/Services"
      />
      {/* Hero Section */}
      <section id="hero" className="min-h-screen flex items-center relative overflow-hidden py-20 bg-white">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10 w-full">
          <div className="mb-16">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 leading-tight text-left">
              Complete Suite of
              <br />
              <span className="bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 bg-clip-text text-transparent">
                Tech Solutions
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-800 mb-8 max-w-3xl text-left">
              From custom development to managed consulting, we deliver transparent, functional software and scalable business solutions.
            </p>
          </div>
          {/* Industry Solutions */}
          <div className="mt-20">
            {industrySolutions.map((solution, index) => (
              <IndustrySolutionSection key={solution.id} solution={solution} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Venture Studio Section */}
<section id="ventures" className="py-24 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

    {/* Heading & Description */}
    <div className="mb-16 text-left">
      <h2 className="text-4xl md:text-5xl font-bold mb-4">
        <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
          Venture Studio
        </span>
      </h2>
      <p className="text-xl text-gray-700 max-w-3xl">
        Join our global community and transform your enterprise through equity-based and retainer partnership models.
      </p>
    </div>

    {/* Main Grid: Left = Steps 1-4, Right = Co-Creation Panel */}
    <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

      {/* Left: Steps 1-4 in 2x2 grid */}
      <div className="grid grid-cols-2 gap-6">
        {ventureSteps.map((step, index) => (
          <div key={index} className="bg-white p-6 rounded-xl border-2 border-purple-200 hover:border-purple-400 transition-all group shadow-lg hover:shadow-xl">
            <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
              {step.number}
            </div>
            <h3 className="text-xl font-bold mb-2 text-gray-800 group-hover:text-purple-600 transition-colors">
              {step.title}
            </h3>
            <p className="text-gray-600 text-sm">{step.description}</p>
          </div>
        ))}
      </div>

      {/* Right: Industry-Leading Co-Creation Panel */}
      <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl border-2 border-purple-200 shadow-xl">
        <Rocket className="w-16 h-16 text-purple-500 mb-6" />
        <h3 className="text-2xl font-bold mb-4 text-gray-800">Industry-Leading Co-Creation</h3>
        <p className="text-gray-700 mb-6">
          Leverage our venture studio for collaborative innovation. We partner with the world's best tech minds and digital innovators to deliver transparent, functional solutions.
        </p>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-purple-500 mt-1">→</span>
            <span>Equity-based partnerships</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-500 mt-1">→</span>
            <span>Retainer models available</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-purple-500 mt-1">→</span>
            <span>End-to-end support from concept to exit</span>
          </li>
        </ul>
      </div>

    </div>
  </div>
</section>

      {/* Playground Section */}
      <section id="playground" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-16">
  <h2 className="text-4xl md:text-5xl font-bold mb-4">
    <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
      STRAY Playground
    </span>
  </h2>
  <p className="text-xl text-gray-600 max-w-3xl">
    Explore hands-on samples of how STRAY is redefining documentation, ERP, and domain-specific AI/ML, from construction tech to fintech and creative industries.
  </p>
</div>


          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-8 rounded-xl border-2 border-purple-200 hover:border-purple-400 transition-all shadow-lg hover:shadow-xl">
              <div className="text-purple-500 text-4xl mb-4">🚀</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Interactive Demos</h3>
              <p className="text-gray-600 mb-6">
                Experience our cutting-edge solutions through interactive demonstrations showcasing real-world applications.
              </p>
              <button
                onClick={() => navigate('/library')}
                className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg font-semibold transition-colors flex items-center gap-2 w-full justify-center text-white"
              >
                View Demos <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gradient-to-br from-pink-50 to-purple-50 p-8 rounded-xl border-2 border-purple-200 hover:border-pink-400 transition-all shadow-lg hover:shadow-xl">
              <div className="text-pink-500 text-4xl mb-4">📊</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Case Studies</h3>
              <p className="text-gray-600 mb-6">
                Dive deep into real-world success stories and see how we've transformed businesses with innovative solutions.
              </p>
              <button
                onClick={() => navigate('/playground', { state: { filter: 'case-studies' } })}
                className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 rounded-lg font-semibold transition-colors flex items-center gap-2 w-full justify-center text-white"
              >
                Read Stories <ArrowRight className="w-5 h-5" />
              </button>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-purple-50 p-8 rounded-xl border-2 border-purple-200 hover:border-blue-400 transition-all shadow-lg hover:shadow-xl">
              <div className="text-blue-500 text-4xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-800">Industry Solutions</h3>
              <p className="text-gray-600 mb-6">
                Explore domain-specific applications from construction tech to fintech and creative industries.
              </p>
              <button
                onClick={() => navigate('/playground', { state: { filter: 'industry-solutions' } })}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 rounded-lg font-semibold transition-colors flex items-center gap-2 w-full justify-center text-white"
              >
                Explore More <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
